import {MahkemeEntity} from "@/entity/MahkemeEntity";

/**
 * Mahkeme seçim modalının (MahkemeForm) hiyerarşik alanları.
 *
 * Listeler birbirine bağlı: yargı birimleri ilam türüne, adliyeler yalnız şehre, mahkemeler
 * adliye + şehir + yargı birimine göre süzülür (YargiBirimleriPicker, AdliyePicker,
 * MahkemePicker).
 */
export interface MahkemeHiyerarsisi {
    ilam: number | null;
    yargiBirimi: number | null;
    sehir: number | null;
    adliye: number | null;
    mahkeme: MahkemeEntity | null;
}

export type MahkemeUstAlani = 'ilam' | 'yargiBirimi' | 'sehir' | 'adliye';

/** Bir alan değişince listesi o alana göre süzüldüğü için geçersiz kalan alt alanlar. */
const ALT_ALANLAR: Record<MahkemeUstAlani, Array<keyof MahkemeHiyerarsisi>> = {
    ilam: ['yargiBirimi', 'mahkeme'],
    yargiBirimi: ['mahkeme'],
    sehir: ['adliye', 'mahkeme'],
    adliye: ['mahkeme'],
};

function idVeyaNull(deger: any): number | null {
    if (deger === null || deger === undefined || deger === '')
        return null;
    const id = Number(deger);
    return Number.isFinite(id) ? id : null;
}

/**
 * Üst alan değişince temizlenmesi gereken alt alanlar.
 *
 * Önceki değer boşsa hiçbir şey temizlenmez: bu bir değişiklik değil ilk doldurmadır
 * (düzenleme ön doldurması, çağıran formun varsayılanları ya da picker'ların boş değer
 * yayınlaması). Aksi halde ör. varsayılan Yargı Birimi, varsayılan İlam Türü atanırken silinirdi.
 */
export function temizlenecekAltAlanlar(alan: MahkemeUstAlani, yeni: any, eski: any): Array<keyof MahkemeHiyerarsisi> {
    const eskiId = idVeyaNull(eski);
    if (eskiId === null || eskiId === idVeyaNull(yeni))
        return [];
    return ALT_ALANLAR[alan];
}

/**
 * Kayıt ilişkileriyle mi geldi? GET /api/v1/mahkeme listesi ve /mahkeme/filter ilişkileri
 * (yargi_birimi, sehir, adliye) taşır; alacağın ham satırından türeyen kayıtlar taşımaz.
 * İlişki anahtarı var ama null ise (kırık yargi_birimi_id) kaydı yeniden istemek bir şey
 * değiştirmez, o yüzden yüklü sayılır.
 */
export function mahkemeIliskileriYuklu(mahkeme: any): boolean {
    return !!mahkeme && idVeyaNull(mahkeme.id) !== null && mahkeme.yargi_birimi !== undefined;
}

/**
 * Kayıtlı mahkemeden modal alanlarını kurar (DEV-2261).
 *
 * Listesinde bulunamayacak bir değer yazılmaz; kullanıcı seçime o kırılımdan devam eder:
 *  - yargı birimi kaydı olmayan mahkemelerde (ör. İnfaz Hakimliklerindeki kırık
 *    yargi_birimi_id) İlam Türü ve Yargı Birimi boş kalır,
 *  - adliyesi başka şehre bağlı mahkemelerde (bazı Bölge Adliye daireleri) Adliye boş kalır,
 *  - Mahkeme yalnız dört üst alan da dolduğunda yazılır; liste dördüne göre süzüldüğü için
 *    aksi halde (ör. adliyesi olmayan idare mahkemeleri) picker'da görünmezdi.
 */
export function mahkemeHiyerarsisiOlustur(mahkeme: any): MahkemeHiyerarsisi | null {
    if (!mahkeme || idVeyaNull(mahkeme.id) === null)
        return null;

    const yargiBirimi = mahkeme.yargi_birimi || null;
    const ilam = yargiBirimi ? idVeyaNull(yargiBirimi.ilam_turu_id) : null;
    const yargiBirimiId = ilam !== null ? idVeyaNull(mahkeme.yargi_birimi_id ?? yargiBirimi.id) : null;
    const sehir = idVeyaNull(mahkeme.sehir_id ?? mahkeme.sehir?.id);
    let adliye = idVeyaNull(mahkeme.adliye_id ?? mahkeme.adliye?.id);
    const adliyeninSehri = idVeyaNull(mahkeme.adliye?.sehir_id);
    if (adliye !== null && adliyeninSehri !== null && adliyeninSehri !== sehir)
        adliye = null;

    const tam = ilam !== null && yargiBirimiId !== null && sehir !== null && adliye !== null;
    return {ilam, yargiBirimi: yargiBirimiId, sehir, adliye, mahkeme: tam ? mahkeme : null};
}
